import {useEffect, useState} from "react";
import {ReactIsland, type WithCommandListener} from "@/shared/reactIsland";
import {TerminalQrCodeCard, type TerminalQrCodeUser} from "@/features/user/terminalQrCodeCard";

export type TerminalQrCodeCardIslandCommands = {
	/** Whose code to show; `null` renders nothing. Cheap — it does not re-read the token. */
	showUser: TerminalQrCodeUser | null;
	/** Re-reads the token state, for a host that reopens on a user whose code may have changed. */
	reloadToken: void;
};


export function TerminalQrCodeCardIsland({
											 addCommandListener,
										 }: WithCommandListener<TerminalQrCodeCardIslandCommands>) {
	const [user, setUser] = useState<TerminalQrCodeUser | null>(null);
	const [reloadCount, setReloadCount] = useState(0);

	useEffect(() => addCommandListener("showUser", setUser), [addCommandListener]);
	useEffect(
		() => addCommandListener("reloadToken", () => setReloadCount((count) => count + 1)),
		[addCommandListener],
	);

	if (user == null) {
		return <></>;
	}

	/*
	 * Remounting on either half of the key drops the previous user's token state instead of
	 * leaving it on screen during the fetch. Keeping the id in the key is what makes a repeated
	 * `showUser` for the same user free of a reload, so the host can feed it a fresh name and
	 * login without the code flickering.
	 */
	return <TerminalQrCodeCard key={`${user.id}-${reloadCount}`} user={user}/>;
}

export function mountTerminalQrCodeCardIsland(
	mountId: string,
): ReactIsland<object, TerminalQrCodeCardIslandCommands> | undefined {
	const mount = document.getElementById(mountId);
	if (!mount) {
		return undefined;
	}

	return new ReactIsland<object, TerminalQrCodeCardIslandCommands>(
		TerminalQrCodeCardIsland,
		{},
		mount,
	);
}
